import { useTranslation } from 'react-i18next';
import { formatDateLocale } from '../../utils/dateFormat';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Zap, Target, Copy, X, ArrowRight,
  ArrowUpDown, Loader2, BarChart3, TrendingUp,
  ChevronDown, ChevronUp, AlertTriangle, Lock, Trash2, Eye, Upload
} from 'lucide-react';
import api, { apiErrorMessage } from '../../utils/api';
import { getActiveWorkspaceClinicId } from '../../utils/activeWorkspace';
import { CampaignCardFull } from '../../components/CampaignRenderers/CanonicalCampaignRenderers';

const STATUS_CLS = {
  active: 'bg-emerald-100 text-emerald-700',
  scheduled: 'bg-blue-100 text-blue-700',
  expired: 'bg-slate-100 text-slate-500',
  draft: 'bg-amber-100 text-amber-700',
};
// COMMERCIAL-GOV-01 (2026-06-16) — orthogonal moderation lifecycle.
// Independent dimension from `visibility`.  Patient surfaces only render
// rows where moderation_state is approved (or absent / legacy).
const MOD_STATUS_CLS = {
  draft:          'bg-slate-100 text-slate-600',
  pending_review: 'bg-amber-50 text-amber-700 border border-amber-200',
  approved:       'bg-blue-50 text-blue-700',
  rejected:       'bg-red-50 text-red-600',
};
const MOD_STATUS_LABEL = {
  draft:          'Draft',
  pending_review: 'Pending Review',
  approved:       'Approved',
  rejected:       'Rejected',
};
const HEALTH_CLS = {
  Strong: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Moderate: 'bg-amber-100 text-amber-700 border-amber-200',
  Weak: 'bg-slate-100 text-slate-500 border-slate-200',
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`;
};

/* ════════════════════════════════════════════════════════════════
   CAMPAIGN DRAWER (Create / Edit / Duplicate) — OT-0A.2-FE1
   ════════════════════════════════════════════════════════════════ */
const CURRENCY_OPTS = ['EUR', 'USD', 'GBP', 'TRY'];
const OFFER_TYPES = [
  { v: 'percentage', l: 'Percent off',  hint: 'e.g. 20% off provider treatment price' },
  { v: 'fixed',      l: 'Fixed price',  hint: 'Flat campaign price in selected currency' },
  { v: 'package',    l: 'Package',      hint: 'Bundled commercial package with included services' },
];

function CampaignDrawer({ open, onClose, onSaved, campaign, providerTreatments, treatmentsState = 'ready', onRetryTreatments }) {
  const { t } = useTranslation();
  const isEdit = !!(campaign?.campaign_id && !campaign._duplicate);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');
  const [form, setForm] = useState({
    title: '', treatment_type: '', short_badge_text: '', description: '',
    discount_type: 'percentage', discount_value: 0,
    currency: 'EUR', original_price_hint: '', campaign_price_hint: '',
    campaign_items: [],
    max_redemptions: '',
    start_at_utc: '', end_at_utc: '', is_enabled: true,
    hero_image_url: '',  // S04-READINESS-GATE-01 — additive
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setApiError('');
    const now = new Date();
    const in14 = new Date(now.getTime() + 14 * 86400000);
    if (campaign) {
      setForm({
        title: campaign._duplicate ? `${campaign.title} (Copy)` : (campaign.title || ''),
        treatment_type: campaign.treatment_type || '',
        short_badge_text: campaign.short_badge_text || '',
        description: campaign.description || '',
        discount_type: normalizeIncomingDiscountType(campaign.discount_type) || 'percentage',
        discount_value: campaign.discount_value || 0,
        currency: campaign.currency || 'EUR',
        original_price_hint: campaign.original_price_hint != null ? String(campaign.original_price_hint) : '',
        campaign_price_hint: campaign.campaign_price_hint != null ? String(campaign.campaign_price_hint) : '',
        campaign_items: Array.isArray(campaign.campaign_items)
          ? campaign.campaign_items.map(normalizeIncomingItem).filter(Boolean)
          : [],
        max_redemptions: campaign._duplicate ? '' : (campaign.max_redemptions != null ? String(campaign.max_redemptions) : ''),
        start_at_utc: campaign._duplicate ? toLocalInput(now) : toLocalInput(campaign.start_at_utc),
        end_at_utc: campaign._duplicate ? toLocalInput(in14) : toLocalInput(campaign.end_at_utc),
        is_enabled: campaign._duplicate ? false : (campaign.is_enabled ?? true),
        hero_image_url: campaign.hero_image_url || '',
      });
    } else {
      setForm({
        title: '', treatment_type: '', short_badge_text: '', description: '',
        discount_type: 'percentage', discount_value: 0,
        currency: 'EUR', original_price_hint: '', campaign_price_hint: '',
        campaign_items: [],
        max_redemptions: '',
        start_at_utc: toLocalInput(now), end_at_utc: toLocalInput(in14), is_enabled: true,
        hero_image_url: '',
      });
    }
    setStep(1);
    setErrors({});
  }, [open, campaign]);

  // When the chosen treatment changes, default currency from the provider treatment
  // and (only on create) seed `original_price_hint` from `price_max` if blank.
  useEffect(() => {
    if (!form.treatment_type) return;
    const t = (providerTreatments || []).find(x => x.treatment_slug === form.treatment_type);
    if (!t) return;
    setForm(prev => ({
      ...prev,
      currency: prev.currency || t.currency || 'EUR',
      original_price_hint: prev.original_price_hint || (t.price_max != null ? String(t.price_max) : ''),
    }));
  }, [form.treatment_type, providerTreatments]);

  // P0-CAMPAIGN-CREATION-CLOSURE-01 (§5) — a local datetime the browser
  // cannot parse must become a FIELD ERROR, never an uncaught
  // `Invalid Date.toISOString()` RangeError inside buildBody().
  const isValidLocalDate = (v) => !!v && !Number.isNaN(new Date(v).getTime());

  // P0-CAMPAIGN-CREATION-CLOSURE-01 (§4) — returns the errors of THIS
  // invocation. The caller must not read the `errors` state it just set:
  // React state updates are asynchronous, so `handleSave` was choosing the
  // wizard step from the PREVIOUS validation result.
  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Required';
    if (!form.treatment_type.trim()) e.treatment_type = 'Pick one of your treatments';
    if (form.discount_value < 0) e.discount_value = 'Must be >= 0';
    if (form.discount_type === 'percentage' && form.discount_value > 100) e.discount_value = 'Max 100%';
    if ((form.discount_type === 'fixed' || form.discount_type === 'package')) {
      if (!form.campaign_price_hint || Number(form.campaign_price_hint) <= 0) e.campaign_price_hint = 'Required (> 0) for this offer type';
      if (!form.currency) e.currency = 'Required for this offer type';
    }
    if (form.start_at_utc && !isValidLocalDate(form.start_at_utc)) e.start_at_utc = t('provider.campaigns.wizard.invalidDate');
    if (form.end_at_utc && !isValidLocalDate(form.end_at_utc)) e.end_at_utc = t('provider.campaigns.wizard.invalidDate');
    if (!e.start_at_utc && !e.end_at_utc && form.start_at_utc && form.end_at_utc
        && new Date(form.end_at_utc) <= new Date(form.start_at_utc)) e.end_at_utc = 'End must be after start';
    if (form.max_redemptions !== '' && (isNaN(form.max_redemptions) || Number(form.max_redemptions) < 1)) e.max_redemptions = 'Must be positive or empty';
    setErrors(e);
    return e;
  };

  const buildBody = () => ({
    title: form.title.trim(),
    treatment_type: form.treatment_type,
    short_badge_text: form.short_badge_text.trim() || undefined,
    description: form.description.trim() || undefined,
    discount_type: form.discount_type,
    discount_value: Number(form.discount_value) || 0,
    currency: form.currency || 'EUR',
    original_price_hint: form.original_price_hint !== '' ? Number(form.original_price_hint) : null,
    campaign_price_hint: form.campaign_price_hint !== '' ? Number(form.campaign_price_hint) : null,
    campaign_items: (form.campaign_items || [])
      .filter(it => (it.label || '').trim())
      .map(it => ({
        label: it.label.trim(),
        included: it.included !== false,
        note: (it.note || '').trim() || undefined,
      })),
    max_redemptions: form.max_redemptions !== '' ? Number(form.max_redemptions) : null,
    start_at_utc: isValidLocalDate(form.start_at_utc) ? new Date(form.start_at_utc).toISOString() : undefined,
    end_at_utc: isValidLocalDate(form.end_at_utc) ? new Date(form.end_at_utc).toISOString() : undefined,
    is_enabled: form.is_enabled,
    hero_image_url: form.hero_image_url || null,
  });

  const handleSave = async () => {
    setApiError('');
    const v = validate();
    if (Object.keys(v).length) {
      // Jump to the first step that has an error — read THIS validation's
      // result, not the asynchronously-updated `errors` state.
      if (v.title || v.treatment_type) setStep(1);
      else if (v.discount_value || v.campaign_price_hint || v.currency || v.max_redemptions) setStep(2);
      else if (v.start_at_utc || v.end_at_utc) setStep(3);
      return;
    }
    setSaving(true);
    const body = buildBody();
    try {
      const url = isEdit
        ? `/clinic/campaigns/update/${campaign.campaign_id}`
        : `/clinic/campaigns/create`;
      const res = await api.post(url, body);
      const d = res?.data || {};
      if (d.success) {
        toast.success(isEdit ? 'Campaign updated' : 'Campaign created');
        onSaved(d.campaign);
        onClose();
        return;
      }
      // Backend returned 2xx but with success=false (defensive)
      const msg = apiErrorMessage({ response: { data: d } }, 'Operation failed');
      setApiError(msg);
      toast.error(msg);
    } catch (err) {
      const resp = err?.response;
      const status = resp?.status;
      const data = resp?.data || {};
      let msg = apiErrorMessage(err, 'Operation failed');
      if (data.error === 'TX_QUOTA_CAMPAIGNS_MONTHLY_EXCEEDED') {
        msg = `Monthly creation limit reached (${data.current}/${data.limit}).`;
      } else if (data.error === 'TX_QUOTA_CAMPAIGNS_ACTIVE_EXCEEDED') {
        msg = `Active campaign limit reached (${data.current}/${data.limit}). Upgrade your plan.`;
      } else if (data.error === 'TX_QUOTA_FEATURE_DISABLED') {
        msg = 'Campaigns are not available on your current plan.';
      } else if (status === 403) {
        msg = msg || 'You do not have permission to perform this action.';
      }
      setApiError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    setApiError('');
    // Run light per-step validation
    if (step === 1) {
      const e = {};
      if (!form.title.trim()) e.title = 'Required';
      if (!form.treatment_type.trim()) e.treatment_type = 'Pick one of your treatments';
      setErrors(e);
      if (Object.keys(e).length) return;
    } else if (step === 2) {
      const e = {};
      if (form.discount_value < 0) e.discount_value = 'Must be >= 0';
      if (form.discount_type === 'percentage' && form.discount_value > 100) e.discount_value = 'Max 100%';
      if ((form.discount_type === 'fixed' || form.discount_type === 'package')) {
        if (!form.campaign_price_hint || Number(form.campaign_price_hint) <= 0) e.campaign_price_hint = 'Required (> 0) for this offer type';
        if (!form.currency) e.currency = 'Required for this offer type';
      }
      setErrors(e);
      if (Object.keys(e).length) return;
    }
    setStep(s => s + 1);
  };

  const addItem = () => {
    setForm(f => ({ ...f, campaign_items: [...(f.campaign_items || []), { label: '', included: true, note: '' }] }));
  };
  const updateItem = (idx, patch) => {
    setForm(f => ({
      ...f,
      campaign_items: (f.campaign_items || []).map((it, i) => i === idx ? { ...it, ...patch } : it),
    }));
  };
  const removeItem = (idx) => {
    setForm(f => ({ ...f, campaign_items: (f.campaign_items || []).filter((_, i) => i !== idx) }));
  };

  const selectedTreatment = useMemo(() => {
    return (providerTreatments || []).find(t => t.treatment_slug === form.treatment_type);
  }, [providerTreatments, form.treatment_type]);

  if (!open) return null;

  const treatmentLabel = (t) => {
    const name = t.custom_name || prettifySlug(t.treatment_slug);
    if (t.price_min != null && t.price_max != null) {
      return `${name} · ${t.currency || 'EUR'} ${t.price_min}-${t.price_max}`;
    }
    return name;
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="campaign-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md sm:max-w-lg shadow-xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="bg-[#122033] text-white px-5 py-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold font-[Manrope]" data-testid="drawer-title">
            {isEdit ? 'Edit Campaign' : campaign?._duplicate ? 'Duplicate Campaign' : 'Create Campaign'}
          </h2>
          <Button variant="ghost" size="sm" className="text-white/60 hover:text-white h-7 w-7 p-0" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center gap-1 px-5 py-2 border-b border-slate-100">
          {[1,2,3,4].map(s => (
            <div key={s} className={`flex-1 h-1 rounded-full transition-colors ${step >= s ? 'bg-[#3BA4C5]' : 'bg-slate-100'}`} />
          ))}
          <span className="text-[10px] text-slate-400 ml-2" data-testid="drawer-step-indicator">Step {step}/4</span>
        </div>

        {/* API error banner */}
        {apiError && (
          <div className="mx-5 mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 flex items-start gap-2" data-testid="campaign-form-error">
            <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700 leading-relaxed">{apiError}</p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {step === 1 && (
            <>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{t('provider.campaigns.wizard.treatmentBasics')}</p>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Treatment *</Label>
                <select
                  className="w-full h-9 rounded-md border border-slate-200 bg-white text-sm px-2 text-slate-700"
                  value={form.treatment_type}
                  onChange={(e) => setForm(f => ({ ...f, treatment_type: e.target.value }))}
                  data-testid="select-treatment"
                >
                  <option value="">— Pick one of your active treatments —</option>
                  {(providerTreatments || []).map(t => (
                    <option key={t.treatment_slug} value={t.treatment_slug} data-testid={`treatment-option-${t.treatment_slug}`}>
                      {treatmentLabel(t)}
                    </option>
                  ))}
                </select>
                {selectedTreatment && (selectedTreatment.price_min != null) && (
                  <p className="text-[10px] text-slate-400 mt-1" data-testid="treatment-price-hint">
                    Current price range: {selectedTreatment.currency || 'EUR'} {selectedTreatment.price_min} – {selectedTreatment.price_max}
                  </p>
                )}
                {/* §7 — loading / truly-empty / request-failure are distinct */}
                {treatmentsState === 'loading' && (
                  <p className="text-[10px] text-slate-400 mt-1" data-testid="treatments-loading">
                    {t('provider.campaigns.wizard.treatmentsLoading')}
                  </p>
                )}
                {treatmentsState === 'error' && (
                  <div className="flex items-center gap-2 mt-1" data-testid="treatments-error">
                    <p className="text-[10px] text-red-500">{t('provider.campaigns.wizard.treatmentsLoadFailed')}</p>
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={onRetryTreatments} data-testid="treatments-retry-btn">
                      {t('common.retry')}
                    </Button>
                  </div>
                )}
                {treatmentsState === 'ready' && (!providerTreatments || providerTreatments.length === 0) && (
                  <p className="text-[10px] text-amber-600 mt-1" data-testid="no-treatments-warning">
                    You have no active treatments yet. Add one in Treatments before creating a campaign.
                  </p>
                )}
                {errors.treatment_type && <p className="text-[10px] text-red-500 mt-0.5" data-testid="error-treatment-type">{errors.treatment_type}</p>}
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Campaign Name *</Label>
                <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder={t('provider.campaigns.wizard.shortOfferPlaceholder')} maxLength={200} data-testid="input-title" />
                {errors.title && <p className="text-[10px] text-red-500 mt-0.5" data-testid="error-title">{errors.title}</p>}
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">{t('provider.campaigns.wizard.shortOffer')}<span className="text-slate-400">(badge, max 30)</span></Label>
                <Input value={form.short_badge_text} onChange={(e) => setForm(f => ({ ...f, short_badge_text: e.target.value }))} placeholder="20% OFF" maxLength={30} data-testid="input-badge" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">{t('provider.campaigns.wizard.patientDescription')}</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder={t('provider.campaigns.wizard.descriptionPlaceholder')}
                  rows={3}
                  maxLength={1000}
                  data-testid="input-description"
                />
                <p className="text-[10px] text-slate-400 mt-1">{form.description.length}/1000</p>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{t('provider.campaigns.wizard.offerPricing')}</p>
              <div>
                <Label className="text-xs text-slate-500 mb-1.5 block">{t('provider.campaigns.wizard.offerType')}</Label>
                <div className="flex flex-col gap-2" data-testid="offer-type-group">
                  {OFFER_TYPES.map(o => (
                    <button key={o.v} type="button"
                      className={`text-left px-3 py-2 rounded-md text-xs font-medium border transition-colors ${form.discount_type === o.v ? 'border-[#3BA4C5] bg-[#3BA4C5]/10 text-[#3BA4C5]' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                      onClick={() => setForm(f => ({ ...f, discount_type: o.v }))} data-testid={`offer-type-${o.v}`}>
                      <div className="flex items-center gap-2"><span className="font-semibold">{o.l}</span></div>
                      <p className="text-[10px] text-slate-400 mt-0.5">{o.hint}</p>
                    </button>
                  ))}
                </div>
              </div>

              {form.discount_type === 'percentage' && (
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">{t('provider.campaigns.wizard.percentOff')}</Label>
                  <Input type="number" min={0} max={100} value={form.discount_value}
                    onChange={(e) => setForm(f => ({ ...f, discount_value: parseFloat(e.target.value) || 0 }))} data-testid="input-discount-value" />
                  {errors.discount_value && <p className="text-[10px] text-red-500 mt-0.5" data-testid="error-discount-value">{errors.discount_value}</p>}
                </div>
              )}

              {(form.discount_type === 'fixed' || form.discount_type === 'package') && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Currency *</Label>
                    <select
                      className="w-full h-9 rounded-md border border-slate-200 bg-white text-sm px-2 text-slate-700"
                      value={form.currency}
                      onChange={(e) => setForm(f => ({ ...f, currency: e.target.value }))}
                      data-testid="select-currency"
                    >
                      {CURRENCY_OPTS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {errors.currency && <p className="text-[10px] text-red-500 mt-0.5">{errors.currency}</p>}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Campaign price *</Label>
                    <Input type="number" min={0} step="0.01" value={form.campaign_price_hint}
                      onChange={(e) => setForm(f => ({ ...f, campaign_price_hint: e.target.value }))} placeholder="e.g. 2900" data-testid="input-campaign-price" />
                    {errors.campaign_price_hint && <p className="text-[10px] text-red-500 mt-0.5" data-testid="error-campaign-price">{errors.campaign_price_hint}</p>}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-500 mb-1 block">{t('provider.campaigns.wizard.originalPrice')}</Label>
                <Input type="number" min={0} step="0.01" value={form.original_price_hint}
                  onChange={(e) => setForm(f => ({ ...f, original_price_hint: e.target.value }))} placeholder={t('provider.campaigns.wizard.originalPricePlaceholder')} data-testid="input-original-price" />
              </div>

              <div>
                <Label className="text-xs text-slate-500 mb-1 block">{t('provider.campaigns.wizard.maxRedemptions')}</Label>
                <Input type="number" min={1} value={form.max_redemptions} placeholder={t('provider.campaigns.wizard.unlimited')}
                  onChange={(e) => setForm(f => ({ ...f, max_redemptions: e.target.value }))} data-testid="input-max-redemptions" />
                <p className="text-[10px] text-slate-400 mt-1">{t('provider.campaigns.wizard.maxRedemptionsHelper')}</p>
                {errors.max_redemptions && <p className="text-[10px] text-red-500 mt-0.5" data-testid="error-max-redemptions">{errors.max_redemptions}</p>}
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{t('provider.campaigns.wizard.packageSchedule')}</p>
              {/* S04-READINESS-GATE-01 (2026-06-16) — additive hero image
                  upload widget. Uses the canonical /api/clinic/media
                  endpoint with context_type=clinic_profile (the existing
                  facility media writer); the returned media_id is
                  composed into /api/media/{id}?variant=preview and
                  persisted on campaign.hero_image_url via the canonical
                  update_campaign writer. No new upload service. */}
              <div className="border border-slate-200 rounded-md p-3 space-y-2" data-testid="campaign-hero-image-section">
                <div className="flex items-start gap-3">
                  <div className="flex-none w-24 h-16 rounded-md overflow-hidden bg-slate-100 border">
                    {form.hero_image_url ? (
                      <img src={form.hero_image_url} alt={t('provider.campaigns.wizard.heroPlaceholder')} className="w-full h-full object-cover" data-testid="campaign-hero-image-preview" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300 text-[10px]" data-testid="campaign-hero-image-placeholder">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <Label className="text-xs text-slate-500">{t('provider.campaigns.wizard.heroImage')}</Label>
                    <p className="text-[10px] text-slate-400">
                      PNG / JPG / WEBP, &le; 10 MB. Will appear on patient-facing surfaces after admin approval.
                    </p>
                    <div>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        id={`campaign-hero-input-${form.title || 'new'}`}
                        className="hidden"
                        data-testid="campaign-hero-image-input"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;   // cancelled picker — never a crash
                          if (f.size > 10 * 1024 * 1024) { toast.error(t('provider.campaigns.toasts.imageTooLarge')); e.target.value = ''; return; }
                          try {
                            // P0-CAMPAIGN-CREATION-CLOSURE-01 (§3) — the
                            // canonical media backend requires
                            // `context_id == authorized clinic_id` for
                            // `context_type=clinic_profile`. The previous
                            // source was `localStorage['treatx_clinic_id']`,
                            // a key NOTHING in the app ever writes, so
                            // `context_id` was always omitted and FastAPI
                            // answered 422 for EVERY hero upload. The active
                            // clinic identity already has an authority —
                            // `getActiveWorkspaceClinicId()`, reading the same
                            // keys RBACContext writes — so it is consumed
                            // here instead of a separately-maintained value.
                            // `context_id` remains a REQUEST field only: the
                            // backend still binds the write to the
                            // authenticated clinic_id and rejects a forged
                            // cross-tenant value.
                            const clinicIdForCtx = getActiveWorkspaceClinicId()
                              || (typeof localStorage !== 'undefined' ? (localStorage.getItem('treatx_clinic_id') || '') : '');
                            if (!clinicIdForCtx) {
                              toast.error(t('provider.campaigns.toasts.noClinicContext'));
                              e.target.value = '';
                              return;
                            }
                            const fd = new FormData();
                            fd.append('file', f);
                            fd.append('context_type', 'clinic_profile');
                            fd.append('context_id', clinicIdForCtx);
                            const res = await api.post('/clinic/media', fd, {
                              headers: { 'Content-Type': 'multipart/form-data' },
                            });
                            const mediaId = res?.data?.media_id || res?.data?.media?.media_id;
                            if (mediaId) {
                              const url = `/api/media/${mediaId}?variant=preview`;
                              setForm(prev => ({ ...prev, hero_image_url: url }));
                              toast.success(t('provider.campaigns.toasts.heroUploaded'));
                            } else {
                              toast.error(t('provider.campaigns.toasts.uploadNoMediaId'));
                            }
                          } catch (err) {
                            // §2 — ALWAYS a string. A 422 `detail` array used
                            // to reach the root <Toaster/> as objects and
                            // white-screened the whole application.
                            toast.error(apiErrorMessage(err, t('provider.campaigns.toasts.heroUploadFailed')));
                          } finally {
                            e.target.value = '';
                          }
                        }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-[11px]"
                        onClick={() => document.getElementById(`campaign-hero-input-${form.title || 'new'}`)?.click()}
                        data-testid="campaign-hero-image-upload-btn"
                      >
                        <Upload className="h-3 w-3 mr-1" />
                        {form.hero_image_url ? 'Replace image' : 'Upload image'}
                      </Button>
                      {form.hero_image_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-[11px] text-red-500 ml-1"
                          onClick={() => setForm(prev => ({ ...prev, hero_image_url: '' }))}
                          data-testid="campaign-hero-image-remove-btn"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs text-slate-500">{t('provider.campaigns.wizard.includedItems')}</Label>
                  <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={addItem} data-testid="btn-add-item">
                    <Plus className="h-3 w-3 mr-1" /> Add item
                  </Button>
                </div>
                {(form.campaign_items || []).length === 0 && (
                  <p className="text-[10px] text-slate-400 italic" data-testid="items-empty-hint">
                    Add line items the patient will receive — these will pre-fill the offer/quote later.
                  </p>
                )}
                <div className="space-y-2">
                  {(form.campaign_items || []).map((it, i) => (
                    <div key={i} className="rounded-md border border-slate-200 p-2 space-y-1.5" data-testid={`campaign-item-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={it.included !== false}
                          onCheckedChange={(v) => updateItem(i, { included: !!v })}
                          data-testid={`item-included-${i}`}
                        />
                        <Input
                          value={it.label || ''}
                          onChange={(e) => updateItem(i, { label: e.target.value })}
                          placeholder={t('provider.campaigns.wizard.itemPlaceholder')}
                          maxLength={120}
                          className="h-8 text-xs"
                          data-testid={`item-label-${i}`}
                        />
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-red-500" onClick={() => removeItem(i)} data-testid={`item-remove-${i}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <Input
                        value={it.note || ''}
                        onChange={(e) => updateItem(i, { note: e.target.value })}
                        placeholder={t('provider.campaigns.wizard.itemNotePlaceholder')}
                        maxLength={200}
                        className="h-7 text-[11px] ml-6"
                        data-testid={`item-note-${i}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t border-slate-100 pt-3 mt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-[#122033]">{t('provider.campaigns.wizard.enableImmediately')}</Label>
                  <Switch checked={form.is_enabled} onCheckedChange={(v) => setForm(f => ({ ...f, is_enabled: v }))} data-testid="toggle-enabled" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">{t('provider.campaigns.wizard.start')}</Label>
                  <Input type="datetime-local" value={form.start_at_utc}
                    onChange={(e) => setForm(f => ({ ...f, start_at_utc: e.target.value }))} data-testid="input-start" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">End</Label>
                  <Input type="datetime-local" value={form.end_at_utc}
                    onChange={(e) => setForm(f => ({ ...f, end_at_utc: e.target.value }))} data-testid="input-end" />
                  {errors.end_at_utc && <p className="text-[10px] text-red-500 mt-0.5" data-testid="error-end">{errors.end_at_utc}</p>}
                </div>
              </div>
            </>
          )}

          {step === 4 && (
            <CampaignPreview form={form} treatment={selectedTreatment} />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between">
          {step > 1 ? (
            <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} data-testid="btn-back">{t('provider.campaigns.wizard.back')}</Button>
          ) : <div />}
          {step < 4 ? (
            <Button size="sm" className="bg-[#122033] hover:bg-[#1a2d45] text-white gap-1" onClick={goNext} data-testid="btn-next">
              {step === 3 ? <>{t('provider.campaigns.wizard.preview')}<Eye className="h-3.5 w-3.5" /></> : <>{t('provider.campaigns.wizard.next')}<ArrowRight className="h-3.5 w-3.5" /></>}
            </Button>
          ) : (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={handleSave} disabled={saving} data-testid="btn-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/* Patient-facing preview rendered at step 4.
 *
 * PDP-001 R6F — canonical render parity:
 *   The provider-side patient preview now renders THROUGH the exact
 *   same `CampaignCardFull` component the patient sees on the public
 *   profile.  No mocked / alternate preview markup survives.  The
 *   provider literally sees the patient card with their in-progress
 *   form data piped through the canonical adapter.
 */
function CampaignPreview({ form, treatment }) {
  const tName = treatment
    ? treatment.custom_name || prettifySlug(treatment.treatment_slug)
    : prettifySlug(form.treatment_type);
  // Adapt the editor `form` to the canonical campaign contract.
  const campaign = {
    campaign_id: form.campaign_id || 'preview',
    treatment_type: form.treatment_type,
    title: form.title,
    short_badge_text: form.short_badge_text,
    description: form.description,
    discount_type: form.discount_type,
    discount_value: form.discount_value,
    campaign_price_hint: form.campaign_price_hint,
    original_price_hint: form.original_price_hint,
    currency: form.currency,
    start_at_utc: form.start_at_utc,
    end_at_utc: form.end_at_utc,
    campaign_items: form.campaign_items || [],
  };
  return (
    <div className="space-y-3" data-testid="campaign-preview">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
        Patient-facing preview
      </p>
      <CampaignCardFull
        campaign={campaign}
        index={0}
        treatmentLabel={tName}
        testidPrefix="preview"
      />
      <p className="text-[10px] text-emerald-900/60 italic">
        This is exactly what patients see on your public profile.  On accept,
        the patient will request this campaign; your quote will pre-fill
        from the items above.
      </p>
    </div>
  );
}

function normalizeIncomingDiscountType(v) {
  if (v === 'fixed_amount') return 'fixed';
  if (v === 'package_bonus') return 'package';
  return v;
}

function normalizeIncomingItem(it) {
  if (!it || typeof it !== 'object') return null;
  // Already canonical?
  if ('label' in it) return { label: String(it.label || ''), included: it.included !== false, note: it.note || '' };
  // Legacy `{item_type/description/name}` shape — adapt.
  const label = it.item_type || it.type || it.name || '';
  if (!label) return null;
  return { label: String(label), included: it.included !== false, note: String(it.description || it.note || '') };
}

function prettifySlug(s) {
  if (!s) return '';
  return String(s).split(/[-_]/).filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function toLocalInput(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ════════════════════════════════════════════════════════════════
   PERFORMANCE PANEL
   ════════════════════════════════════════════════════════════════ */
function PerformancePanel({ campaign, onClose }) {
  const { t } = useTranslation();
  if (!campaign) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="performance-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <Card className="relative z-10 w-full max-w-md mx-4 shadow-xl" data-testid="performance-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#3BA4C5]" /> {campaign.title}
            </CardTitle>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose}><X className="h-4 w-4" /></Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <MetricTile label={t('provider.campaigns.metrics.leads')} value={campaign.leads_count ?? 0} />
            <MetricTile label={t('provider.campaigns.metrics.quotes')} value={campaign.quotes_count ?? 0} />
            <MetricTile label={t('provider.campaigns.metrics.bookings')} value={campaign.bookings_count ?? 0} color="text-emerald-700" />
            <MetricTile label={t('provider.campaigns.metrics.conversion')} value={`${campaign.conversion_rate ?? 0}%`} color="text-blue-700" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <MetricTile label={t('provider.campaigns.metrics.redemptions')} value={`${campaign.redemption_count ?? 0}${campaign.max_redemptions != null ? '/' + campaign.max_redemptions : ''}`} />
            <MetricTile label={t('provider.campaigns.metrics.commission')} value={campaign.commission_generated ? `$${(campaign.commission_generated/100).toFixed(2)}` : '$0.00'} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">{t('provider.campaigns.list.healthPrefix')}</span>
            <Badge variant="outline" className={`text-xs px-2 py-0.5 ${HEALTH_CLS[campaign.health_badge] || ''}`} data-testid="perf-health-badge">
              {campaign.health_badge || 'N/A'}
            </Badge>
          </div>
          <p className="text-[10px] text-slate-400">
            {campaign.health_badge === 'Strong' && 'Great! This campaign is converting well.'}
            {campaign.health_badge === 'Moderate' && 'Some activity. Consider optimizing offer or extending the window.'}
            {campaign.health_badge === 'Weak' && 'Low activity. Check treatment type demand and campaign visibility.'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricTile({ label, value, color = 'text-[#122033]' }) {
  return (
    <div className="bg-slate-50 rounded-md px-3 py-2">
      <p className="text-[9px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-bold font-[Manrope] ${color}`}>{value}</p>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════════════════════ */
export default function ClinicCampaignsDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [providerTreatments, setProviderTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);
  const [perfCampaign, setPerfCampaign] = useState(null);
  const [sortKey, setSortKey] = useState('created_at_utc');
  const [sortDir, setSortDir] = useState('desc');
  const [togglingId, setTogglingId] = useState(null);
  const [quota, setQuota] = useState(null);
  // P0-CAMPAIGN-CREATION-CLOSURE-01 (§1) — quota is an AUTHORITY RESPONSE, not
  // an optimistic default. While it is 'loading' the Create button must stay
  // disabled (it used to be briefly clickable because `quota` was null, so
  // every `quota?.…` guard evaluated falsy = "allowed"); on 'error' we offer
  // Retry instead of silently assuming access. Backend enforcement unchanged.
  const [quotaState, setQuotaState] = useState('loading');   // loading | ready | error
  const [treatmentsState, setTreatmentsState] = useState('loading');  // loading | ready | error

  const fetchQuota = useCallback(async () => {
    setQuotaState('loading');
    try {
      const res = await api.get(`/clinic/campaigns/quota`);
      setQuota(res.data);
      setQuotaState('ready');
      return res.data;
    } catch (_) {
      setQuotaState('error');
      return null;
    }
  }, []);

  const fetchProviderTreatments = useCallback(async () => {
    setTreatmentsState('loading');
    try {
      const res = await api.get(`/clinic/treatments`);
      const d = res?.data || {};
      const rows = (d.treatments || d.items || [])
        .filter(t => t.is_active !== false)
        .map(t => ({
          treatment_slug: t.treatment_slug,
          custom_name: t.custom_name || t.master?.name || null,
          currency: t.currency || 'EUR',
          price_min: t.price_min ?? null,
          price_max: t.price_max ?? null,
        }))
        .filter(t => !!t.treatment_slug);
      setProviderTreatments(rows);
      setTreatmentsState('ready');
    } catch (_) {
      // §7 — a REQUEST FAILURE must not look like an empty catalogue.
      setTreatmentsState('error');
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, perfRes] = await Promise.all([
        api.get(`/clinic/campaigns`),
        api.get(`/clinic/campaigns/performance`),
      ]);
      if (listRes?.data) { const d = listRes.data; setCampaigns(d.campaigns || []); }
      if (perfRes?.data) { const d = perfRes.data; setPerformance(d.campaigns || []); }
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
    fetchQuota();
    fetchProviderTreatments();
  }, [fetchQuota, fetchProviderTreatments]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // OT-0A.2-FE1: optimistic insert/update when drawer saves so the list
  // reflects the new campaign immediately (full refresh still runs).
  const handleSaved = useCallback((saved) => {
    if (saved && saved.campaign_id) {
      setCampaigns(prev => {
        const idx = prev.findIndex(c => c.campaign_id === saved.campaign_id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...prev[idx], ...saved };
          return next;
        }
        return [saved, ...prev];
      });
    }
    fetchAll();
  }, [fetchAll]);

  // Merge performance data into campaigns
  const enriched = useMemo(() => {
    const perfMap = {};
    performance.forEach(p => { perfMap[p.campaign_id] = p; });
    return campaigns.map(c => ({ ...c, ...(perfMap[c.campaign_id] || {}) }));
  }, [campaigns, performance]);

  const sorted = useMemo(() => {
    const copy = [...enriched];
    copy.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return copy;
  }, [enriched, sortKey, sortDir]);

  const summary = useMemo(() => {
    const active = enriched.filter(c => c.visibility === 'active').length;
    const totalRedemptions = enriched.reduce((s, c) => s + (c.redemption_count || 0), 0);
    const perfCamps = performance.filter(p => p.health_badge);
    const topHealth = perfCamps.length > 0
      ? (perfCamps.filter(p => p.health_badge === 'Strong').length > perfCamps.length / 2 ? 'Strong'
        : perfCamps.filter(p => p.health_badge === 'Weak').length > perfCamps.length / 2 ? 'Weak' : 'Moderate')
      : 'N/A';
    return { active, totalRedemptions, topHealth };
  }, [enriched, performance]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const handleToggle = async (camp) => {
    if (camp.visibility === 'expired') { toast.error(t('provider.campaigns.toasts.cannotReenableExpired')); return; }
    setTogglingId(camp.campaign_id);
    // Optimistic UI
    setCampaigns(prev => prev.map(c =>
      c.campaign_id === camp.campaign_id
        ? { ...c, is_enabled: !c.is_enabled, visibility: !c.is_enabled ? 'active' : 'draft' }
        : c
    ));
    try {
      const res = await api.post(`/clinic/campaigns/toggle/${camp.campaign_id}`);
      const d = res?.data || {};
      if (d.campaign) {
        setCampaigns(prev => prev.map(c => c.campaign_id === camp.campaign_id ? d.campaign : c));
      }
      fetchQuota();
    } catch (e) {
      // Rollback
      setCampaigns(prev => prev.map(c =>
        c.campaign_id === camp.campaign_id ? camp : c
      ));
      const err = e?.response?.data || {};
      if (err.error === 'TX_QUOTA_CAMPAIGNS_ACTIVE_EXCEEDED') {
        toast.error(`Active campaign limit reached (${err.current}/${err.limit}). Upgrade your plan.`);
      } else if (err.error === 'TX_QUOTA_FEATURE_DISABLED') {
        toast.error(t('provider.campaigns.toasts.featureLocked'));
      } else if (err.error === 'TX_QUOTA_UNAVAILABLE') {
        toast.error(t('provider.campaigns.toasts.serviceUnavailable'));
      } else {
        toast.error(err.detail || `Toggle failed: ${e.message}`);
      }
      fetchQuota();
    } finally { setTogglingId(null); }
  };

  const handleDuplicate = async (camp) => {
    try {
      await api.post(`/clinic/campaigns/${camp.campaign_id}/duplicate`);
      toast.success(t('provider.campaigns.toasts.duplicated'));
      fetchAll();
    } catch (e) {
      const err = e?.response?.data || {};
      if (err.error === 'TX_QUOTA_CAMPAIGNS_MONTHLY_EXCEEDED') {
        toast.error(`Monthly creation limit reached (${err.current}/${err.limit}). Resets ${formatDateLocale(err.reset_at_utc)}.`);
      } else if (err.error === 'TX_QUOTA_FEATURE_DISABLED') {
        toast.error(t('provider.campaigns.toasts.planLocked'));
      } else {
        toast.error(err.detail || `Duplicate failed: ${e.message}`);
      }
      fetchQuota();
    }
  };

  const handleEdit = (camp) => {
    setEditCampaign(camp);
    setDrawerOpen(true);
  };

  // COMMERCIAL-GOV-01 (2026-06-16) — moderation lifecycle actions.
  // Provider-visible "Submit for Review" / "Resubmit" transitions are
  // wired to the canonical TreatmentCampaignService via the additive
  // endpoints. No new client SSOT — list refresh re-pulls from the
  // canonical writer.
  const handleSubmitForReview = async (camp) => {
    try {
      const res = await api.post(`/clinic/campaigns/${camp.campaign_id}/submit`);
      const d = res?.data || {};
      if (d.campaign) {
        setCampaigns(prev => prev.map(c => c.campaign_id === camp.campaign_id ? d.campaign : c));
      }
      toast.success(t('provider.campaigns.toasts.submitted'));
    } catch (e) {
      const err = e?.response?.data || {};
      toast.error(err.detail || `Submit failed: ${e.message}`);
    }
  };

  const handleResubmit = async (camp) => {
    try {
      const res = await api.post(`/clinic/campaigns/${camp.campaign_id}/resubmit`);
      const d = res?.data || {};
      if (d.campaign) {
        setCampaigns(prev => prev.map(c => c.campaign_id === camp.campaign_id ? d.campaign : c));
      }
      toast.success(t('provider.campaigns.toasts.resubmitted'));
    } catch (e) {
      const err = e?.response?.data || {};
      toast.error(err.detail || `Resubmit failed: ${e.message}`);
    }
  };

  const handleCreate = async () => {
    // §1 — re-check eligibility against the canonical authority immediately
    // before the wizard opens, so a stale page cannot start a creation the
    // backend will refuse.
    const fresh = await fetchQuota();
    if (!fresh) {
      toast.error(t('provider.campaigns.toasts.quotaUnavailable'));
      return;
    }
    if (fresh.enforcement_active && fresh.campaigns_enabled === false) {
      toast.error(t('provider.campaigns.list.lockedTitle'));
      return;
    }
    const mLimit = fresh.monthly_limit;
    if (fresh.enforcement_active && mLimit !== null && mLimit !== undefined && mLimit >= 0
        && fresh.monthly_created_current >= mLimit) {
      toast.error(`${t('provider.campaigns.list.createdThisMonth')}${fresh.monthly_created_current}/${mLimit}`);
      return;
    }
    setEditCampaign(null);
    setDrawerOpen(true);
  };

  const handlePerfClick = (camp) => {
    const perf = performance.find(p => p.campaign_id === camp.campaign_id);
    setPerfCampaign(perf || camp);
  };

  // Quota-derived UI state
  const campaignsDisabled = quota?.enforcement_active && quota?.campaigns_enabled === false;
  const monthlyLimitReached = quota?.enforcement_active && quota?.monthly_limit !== null && quota?.monthly_limit !== undefined && quota?.monthly_limit >= 0 && quota?.monthly_created_current >= quota?.monthly_limit;
  const activeLimitReached = quota?.enforcement_active && quota?.active_limit !== null && quota?.active_limit >= 0 && quota?.active_current >= quota?.active_limit;
  // §1 — never clickable before the authority has answered.
  const createDisabled = quotaState !== 'ready' || campaignsDisabled || monthlyLimitReached;

  const COLS = [
    { key: 'title', label: 'Campaign', align: 'left' },
    { key: 'treatment_type', label: 'Treatment', align: 'left' },
    { key: 'visibility', label: 'Status', align: 'center' },
    { key: 'start_at_utc', label: 'Window', align: 'left', noSort: true },
    { key: 'discount_value', label: 'Offer', align: 'left', noSort: true },
    { key: 'redemption_count', label: 'Redeemed', align: 'right' },
    { key: 'actions', label: '', align: 'right', noSort: true },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="clinic-campaigns-dashboard">
      <header className="bg-[#122033] text-white border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10" onClick={() => navigate('/clinic/admin')} data-testid="back-to-clinic">
              <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
            <div className="h-5 w-px bg-white/20" />
            <h1 className="text-lg font-semibold tracking-tight font-[Manrope] flex items-center gap-2">
              <Target className="h-4.5 w-4.5" /> Campaigns
            </h1>
          </div>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 text-xs disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleCreate} disabled={createDisabled} data-testid="create-campaign-btn"
            title={quotaState === 'loading' ? t('provider.campaigns.list.quotaLoading') : quotaState === 'error' ? t('provider.campaigns.list.quotaUnavailable') : campaignsDisabled ? 'Campaigns not available on your plan' : monthlyLimitReached ? `Monthly limit reached (${quota?.monthly_created_current}/${quota?.monthly_limit})` : ''}>
            {createDisabled ? <Lock className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} Create Campaign
          </Button>
          <Button size="sm" variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 gap-1.5 text-xs" onClick={() => navigate('/clinic/campaigns/performance')} data-testid="campaign-performance-btn">
            <BarChart3 className="h-3.5 w-3.5" /> Performance
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* §1 — quota authority unavailable: offer Retry, never assume access */}
        {quotaState === 'error' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3" data-testid="quota-error-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">{t('provider.campaigns.list.quotaUnavailable')}</p>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={fetchQuota} data-testid="quota-retry-btn">
              {t('common.retry')}
            </Button>
          </div>
        )}

        {/* Upgrade Banner */}
        {campaignsDisabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="upgrade-banner">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">{t('provider.campaigns.list.lockedTitle')}</p>
              <p className="text-xs text-amber-600 mt-0.5">{t('provider.campaigns.list.lockedSubtitle')}</p>
            </div>
          </div>
        )}

        {/* Quota Info Strip */}
        {quota?.enforcement_active && !campaignsDisabled && (
          <div className="flex items-center gap-4 text-[10px] text-slate-400" data-testid="quota-info-strip">
            <span>{t('provider.campaigns.list.activePrefix')}<span className={`font-semibold ${activeLimitReached ? 'text-amber-600' : 'text-slate-600'}`}>{quota.active_current}{quota.active_limit >= 0 ? `/${quota.active_limit}` : '/\u221E'}</span></span>
            <span className="h-3 w-px bg-slate-200" />
            <span>{t('provider.campaigns.list.createdThisMonth')}<span className={`font-semibold ${monthlyLimitReached ? 'text-amber-600' : 'text-slate-600'}`}>{quota.monthly_created_current}{quota.monthly_limit !== null && quota.monthly_limit !== undefined ? `/${quota.monthly_limit}` : '/\u221E'}</span></span>
            {quota.reset_at_utc && <span className="text-slate-300">Resets {formatDateLocale(quota.reset_at_utc)}</span>}
          </div>
        )}

        {/* Summary Strip */}
        <div className="grid grid-cols-3 gap-3" data-testid="summary-strip">
          <Card className="border border-slate-200/60 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[10px] text-emerald-500 uppercase tracking-wider">{t('provider.campaigns.list.colActive')}</p>
              <p className="text-xl font-bold text-emerald-700 font-[Manrope]" data-testid="summary-active">{summary.active}</p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200/60 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[10px] text-blue-500 uppercase tracking-wider">{t('provider.campaigns.list.colRedemptions')}</p>
              <p className="text-xl font-bold text-blue-700 font-[Manrope]" data-testid="summary-redemptions">{summary.totalRedemptions}</p>
            </CardContent>
          </Card>
          <Card className="border border-slate-200/60 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">{t('provider.campaigns.list.colHealth')}</p>
              <Badge variant="outline" className={`text-xs mt-1 ${HEALTH_CLS[summary.topHealth] || ''}`} data-testid="summary-health">{summary.topHealth}</Badge>
            </CardContent>
          </Card>
        </div>

        {/* Helper */}
        <p className="text-[11px] text-slate-400" data-testid="helper-text">
          Campaign becomes visible in patient search when Active + Public.
        </p>

        {/* Table */}
        {loading ? (
          <div className="space-y-2" data-testid="campaigns-loading">{[1,2,3,4].map(i => <div key={i} className="h-12 bg-slate-100 animate-pulse rounded" />)}</div>
        ) : sorted.length === 0 ? (
          <Card className="border border-slate-200/60"><CardContent className="p-10 text-center">
            <Target className="h-8 w-8 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-400" data-testid="empty-campaigns">{t('provider.campaigns.list.empty')}</p>
          </CardContent></Card>
        ) : (
          <div className="overflow-x-auto" data-testid="campaigns-table-wrapper">
            <table className="w-full text-sm" data-testid="campaigns-table">
              <thead>
                <tr className="border-b border-slate-100">
                  {COLS.map(col => (
                    <th key={col.key}
                      className={`py-2 text-[10px] uppercase text-slate-400 font-medium ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'} ${!col.noSort ? 'cursor-pointer select-none hover:text-slate-600' : ''}`}
                      onClick={() => !col.noSort && handleSort(col.key)}>
                      <span className="inline-flex items-center gap-0.5">
                        {col.label}
                        {!col.noSort && sortKey === col.key && <ArrowUpDown className="h-2.5 w-2.5 text-[#3BA4C5]" />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((c, i) => {
                  const isExpired = c.visibility === 'expired';
                  return (
                    <tr key={c.campaign_id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`campaign-row-${i}`}>
                      <td className="py-2.5">
                        <button className="text-xs font-medium text-[#122033] hover:text-[#3BA4C5] transition-colors text-left max-w-[200px] truncate block" onClick={() => handleEdit(c)} title={`Edit ${c.title}`} data-testid={`campaign-name-${i}`}>
                          {c.title}
                        </button>
                        {c.short_badge_text && <span className="text-[10px] text-slate-400">{c.short_badge_text}</span>}
                      </td>
                      <td className="py-2.5 text-xs text-slate-500 font-mono">{c.treatment_type}</td>
                      <td className="py-2.5 text-center">
                        <Badge className={`text-[10px] px-1.5 py-0 ${STATUS_CLS[c.visibility] || ''}`} data-testid={`campaign-status-${i}`}>{c.visibility}</Badge>
                        {(() => {
                          const mod = c.moderation_state || 'approved';
                          if (mod === 'approved') return null;
                          return (
                            <Badge
                              className={`text-[9px] px-1.5 py-0 ml-1 ${MOD_STATUS_CLS[mod] || ''}`}
                              data-testid={`campaign-moderation-${i}`}
                            >
                              {MOD_STATUS_LABEL[mod] || mod}
                            </Badge>
                          );
                        })()}
                      </td>
                      <td className="py-2.5 text-[11px] text-slate-500">{fmtDate(c.start_at_utc)} - {fmtDate(c.end_at_utc)}</td>
                      <td className="py-2.5 text-xs text-slate-600">
                        {c.discount_type === 'percentage' ? `${c.discount_value}%`
                          : (c.discount_type === 'fixed' || c.discount_type === 'fixed_amount') ? `${c.currency || 'EUR'} ${c.campaign_price_hint ?? c.discount_value}`
                          : (c.discount_type === 'package' || c.discount_type === 'package_bonus') ? `${c.currency || 'EUR'} ${c.campaign_price_hint ?? ''}`
                          : c.discount_value || '-'}
                        {c.discount_type === 'percentage' && <span className="text-[10px] text-slate-400 ml-0.5">off</span>}
                        {(c.discount_type === 'package' || c.discount_type === 'package_bonus') && <span className="text-[10px] text-slate-400 ml-0.5">package</span>}
                        {(c.discount_type === 'fixed' || c.discount_type === 'fixed_amount') && <span className="text-[10px] text-slate-400 ml-0.5">fixed</span>}
                      </td>
                      <td className="py-2.5 text-right text-xs text-slate-600" data-testid={`campaign-redemptions-${i}`}>
                        {c.redemption_count ?? 0}{c.max_redemptions != null ? `/${c.max_redemptions}` : '/\u221E'}
                      </td>
                      <td className="py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-[#3BA4C5]" onClick={() => handlePerfClick(c)} title={t('provider.campaigns.aria.performance')} data-testid={`perf-btn-${i}`}>
                            <BarChart3 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-400 hover:text-[#122033] px-1.5" onClick={() => handleEdit(c)} data-testid={`edit-btn-${i}`}>{t('provider.campaigns.aria.edit')}</Button>
                          {(c.moderation_state || 'approved') === 'draft' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-[10px] text-[#3BA4C5] hover:bg-[#3BA4C5]/10 px-1.5"
                              onClick={() => handleSubmitForReview(c)}
                              data-testid={`submit-review-btn-${i}`}
                              title={t('provider.campaigns.aria.submitForReview')}
                            >
                              Submit
                            </Button>
                          )}
                          {(c.moderation_state) === 'rejected' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-[10px] text-red-500 hover:bg-red-50 px-1.5"
                              onClick={() => handleResubmit(c)}
                              data-testid={`resubmit-btn-${i}`}
                              title={t('provider.campaigns.aria.resubmitForReview')}
                            >
                              Resubmit
                            </Button>
                          )}
                          <Switch
                            checked={c.is_enabled}
                            onCheckedChange={() => handleToggle(c)}
                            disabled={isExpired || togglingId === c.campaign_id || (activeLimitReached && !c.is_enabled)}
                            title={activeLimitReached && !c.is_enabled ? `Active limit reached (${quota?.active_current}/${quota?.active_limit})` : ''}
                            data-testid={`toggle-btn-${i}`}
                          />
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-[#3BA4C5]" onClick={() => handleDuplicate(c)} title={t('provider.campaigns.aria.duplicate')} data-testid={`duplicate-btn-${i}`}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[10px] text-slate-300 mt-2 text-right">{sorted.length} campaign{sorted.length !== 1 ? 's' : ''}</p>
          </div>
        )}
      </main>

      {/* Drawer */}
      <CampaignDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditCampaign(null); }}
        onSaved={handleSaved}
        campaign={editCampaign}
        providerTreatments={providerTreatments}
        treatmentsState={treatmentsState}
        onRetryTreatments={fetchProviderTreatments}
      />

      {/* Performance Panel */}
      <PerformancePanel campaign={perfCampaign} onClose={() => setPerfCampaign(null)} />
    </div>
  );
}
